import { Observable,
         BehaviorSubject,
         ReplaySubject } from 'rxjs'
import { distinctUntilChanged,
         filter,
         map,
         mergeMap } from 'rxjs/operators'

import { Inject, Injectable } from '@angular/core'

import { Title } from '@angular/platform-browser'

import { ActivatedRoute,
         Router,
         NavigationExtras,
         NavigationEnd,
         PRIMARY_OUTLET,
         UrlTree,
         UrlSegment,
         UrlSegmentGroup } from '@angular/router'

import { AppStatic } from '../_helpers/app-static'
import { Outlets } from '../_models/outlets.model'
import { APP_BASE_HREF } from '@angular/common'


@Injectable()
export class RouterService {

  // private keyboardResponseSubject = new BehaviorSubject<string>(null)
  private keyboardResponseSubject = new ReplaySubject<string>(1)
  public keyboardResponse = this.keyboardResponseSubject.asObservable().pipe(distinctUntilChanged())

  private primaryTitleCustomSubject = new BehaviorSubject<string>(null)
  public primaryTitleCustom = this.primaryTitleCustomSubject.asObservable().pipe(distinctUntilChanged())
  private primaryTitleCustomLocal: string

  private primaryTitleDefaultSubject = new BehaviorSubject<string>(null)
  public primaryTitleDefault = this.primaryTitleDefaultSubject.asObservable().pipe(distinctUntilChanged())
  private primaryTitleDefaultLocal: string

  private urlSubject = new BehaviorSubject<string>(this.router.url)
  public url = this.urlSubject.asObservable().pipe(distinctUntilChanged())
  private urlLocal: string
  private urlBaseLocal: string

  private queryParamsSubject = new BehaviorSubject<string>(this.router.url)
  public queryParams = this.queryParamsSubject.asObservable().pipe(distinctUntilChanged())
  private queryParamsLocal: any

  isNavigating: boolean

  constructor (
    @Inject(APP_BASE_HREF) public baseHref: string,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title
  ) {}
  

  // from https://toddmotto.com/dynamic-page-titles-angular-2-router-events

  navigationEndStream(): Observable<any> {
    return this.router.events.pipe( filter(e => e instanceof NavigationEnd))
  }

  updatePrimaryTitleCustom(title: string) {
    this.primaryTitleCustomSubject.next(title)
    this.primaryTitleCustomLocal = title
    this.setTitleFromPrimarySubjects()
    return title
  }

  updatePrimaryTitleDefault(title) {
    this.primaryTitleDefaultSubject.next(title)
    this.primaryTitleDefaultLocal = title
    this.setTitleFromPrimarySubjects()
    return title
  }

  // updateUrl(url: string) {
  updateUrl() {
    // save url to urlLocal and urlSubject, after removing external queryParams
    let url = this.router.url
    if (this.urlLocal === url) return
    this.urlSubject.next(url)
    this.urlLocal = url
    let out = this.router.url.split('(')[0]
    this.urlBaseLocal = out
    return out
  }

  setTitleFromPrimarySubjects() {
    let newTitle = this.primaryTitleCustomLocal || this.primaryTitleDefaultLocal
    if (newTitle) { this.titleService.setTitle(newTitle) }
  }

  outlets(withPrimary = true): Outlets {
    let urlTree = this.router.parseUrl(this.router.url)
    // let urlTree = this.router.parseUrl(route.url)
    let urlChildren = urlTree.root.children
    let outlets = {} as Outlets
    for (let key of Object.keys(urlChildren)) {
      let segments = urlChildren[key].segments
      let outletPath = segments.map((s: any) => s.path ).join('/')
      outlets[key] = outletPath
    }
    if (!withPrimary) delete outlets['primary'] // = null
    // console.log('outlets', urlTree, this.router.url, outlets)
    return outlets
    // if (AppStatic.objectIsEmpty(outlets)) return null
    // return outlets
  }

  outlet(name: string) {
    return this.outlets()[name]
  }

  outletIsOpen(outlet: string): boolean {
    // let outlets = this.outlets()
    return !!this.outlet(outlet)
  }

  // i.e. updateOutlets({primary: 'path/here', secondary: null}, {'ioid': 'a2o34j2'})
  // updateOutlets(params: Outlets|any, queryParams?: any): Promise<any> {
  updateOutlets(outletParams: Outlets|any, params={}): Promise<any> {
    let queryParams = params['queryParams']

    if (!outletParams) { console.warn('updateOutlets: outletParams needed'); return Promise.resolve() }
    let outlets = this.outlets()

    if (outletParams['secondary'] === null) {
      outlets = { p: null, m: null, l: null, c: null }
    }
    delete outletParams['secondary']

    // for (let key in outletParams) {
    for (const key of Object.keys(outletParams)) {
      // console.log('outletParams[' + key + ']:', outletParams[key])
      if (!this.outletPassCheck(key)) { continue }
      outlets[key] = outletParams[key]
    }
    return AppStatic.delayPromise(0).then(() => {
      // return this.updateOutletsNow(outlets, queryParams)
      return this.updateOutletsNow(outlets, params)
    })
    // setTimeout(this.updateOutletsNow.bind(this, outlets, queryParams), 0)
  }

  // updateOutletsNow(outlets: any, queryParams?: any): Promise<any> {
  updateOutletsNow(outlets: any, params={}): Promise<any> {
    // let queryParams = params['queryParams']
    // let extras = this.paramsWithout(params, 'delay')
    let defaultExtras = { queryParamsHandling: 'merge' } as NavigationExtras
    // if (queryParams) defaultExtras['queryParams'] = queryParams
    let options = Object.assign(defaultExtras, params)
    // console.log('uon', params, options)
    // if (queryParams) { options['queryParams'] = queryParams }
    return this.router.navigate([{ outlets: outlets }], options)
  }

  updateOutlet(outlet: string, newRoute: string, params={}): Promise<any> {
    let delay = params['delay']
    let extras = this.paramsWithout(params, 'delay')

    if (!this.outletPassCheck(outlet)) return
    let outlets = this.outlets()
    outlets[outlet] = newRoute
    // console.log('uo', outlet, newRoute, params, extras)
    // setTimeout(this.updateOutletsNow.bind(this, outlets), delay)
    return AppStatic.delayPromise(0).then(() => {
      return this.updateOutletsNow(outlets, extras)
    })
  }

  paramsWithout(params: any, keys:string|string[]) {
    if (typeof keys === 'string') keys = [keys]
    let without = Object.assign({}, params)
    delete without['delay']
    if (Object.keys(without).length === 0) return
    return without
  }

  navigatePrimary(path: string, queryParams?:any): Promise<any> {
    path = path.replace(/^\//, '') // remove initial slash if any
    // console.log('navPrimary', path)

    // setTimeout(this.updateOutlets.bind(this, { primary: path, secondary: null }, queryParams),  100)
    // return this.updateOutlets({ primary: path, secondary: null }, queryParams)
    return AppStatic.delayPromise(100).then(() => {
      return this.updateOutlets({ primary: path, secondary: null })
    })
  }

  closePopup() {
    return this.closeOutlet('p')
  }

  closeOutlet(outlet: string, params={}) {
    // console.log('router close outlet: ' + outlet, params)
    this.updateOutlet(outlet, null)
  }

  urlArray(url, params) {
    let urlArray: any[] = [url] // [ "/" + url]
    let outlets = params['outlets'] || this.outlets()

    if (outlets) urlArray.push({ outlets: outlets })
    return urlArray
  }

  // extras for 'navigate' NOT navigateByUrl
  navigatePreparedExtras(params: any) {
    // whitelist params; from https://stackoverflow.com/questions/5072136/javascript-filter-for-objects
    let allowed = ['queryParams',
                   'queryParamsHandling',
                   'outlets',
                   'replaceUrl']

    let out = Object.keys(params)
                .filter(key => allowed.includes(key))
                .reduce((obj,key) => {
                  obj[key] = params[key]
                  return obj
                }, {}) as NavigationExtras
    // console.log('navExtras', out)
    return out
  }

  // at navigate: manually merge query params
  navigate(url = '', params = {}): Promise<any> {
    // console.log('NAV', url, params)
    let urlArray = this.urlArray(url, params)
    let navExtras = this.navigatePreparedExtras(params)
    return this.router.navigate(urlArray, navExtras)
    // return setTimeout(this.router.navigate.bind(this, urlArray, options), 100)
  }

  // note: extras here is NavigationBehaviorOptions. (NO QUERY PARAMS)
  // https://angular.io/api/router/Router   // https://angular.io/api/router/NavigationBehaviorOptions
  navigateByUrl(urlTree: (UrlTree|string), extras?:any) {
    if (this.isNavigating) return
    this.isNavigating = true
    // console.log('navByUrl', extras, urlTree)

    return this.router.navigateByUrl(urlTree, extras).then(res => {
      setTimeout(() => { this.isNavigating = false }, 100)
    })
  }

  outletsFromUrlTree(urlTree) {
    let urlTarget = urlTree.parent || urlTree
    if (!urlTarget || !urlTarget.root) return
    let children = urlTarget['root']['children']
    if (!children) return null

    let out = {}
    for (const [outletName, urlSegmentGroup] of Object.entries(children)) {
      if (outletName === 'primary') continue
      let segments = urlSegmentGroup['segments']
      if (!segments || segments.length === 0) continue
      out[outletName] = segments[0]['path']
    }
    return out
  }

  // use: updateQueryParams({atc: null}) ... ({x: 'laibh13'}) ... etc
  updateQueryParams(qp = {}, params = {}): Promise<boolean> {
    // navigate = true, force = false
    if (AppStatic.objectsAreIdentical(this.queryParamsLocal, qp)) return

    let navigate = (typeof params['navigate'] === 'boolean') ? params['navigate'] : true
    let force = params['force'] || false
    // let qpl = Object.assign({}, this.queryParamsLocal) // to fix 'Cannot assign to read only property'
    this.queryParamsLocal = force ? qp : Object.assign({}, this.queryParamsLocal, qp)
    this.queryParamsSubject.next(this.queryParamsLocal)
    if (!navigate) return
    // console.warn('>>> uqp', navigate, qp)
    return this.router.navigate([], { queryParams: this.queryParamsLocal,
                                      queryParamsHandling: 'merge' })
  }

  removeQueryParams(queryParams: string[], params?) {
    let urlTree = this.router.parseUrl(this.router.url)
    // console.log('rm qp', queryParams, urlTree.queryParams)
    let go = false
    let urlQueryParams = urlTree.queryParams
    if (!urlQueryParams) return
    for (let qp of queryParams) { if (urlQueryParams.hasOwnProperty(qp)) go = true }
    if (!go) return

    let nullQueryParams = {}
    for (let qp of queryParams) { nullQueryParams[qp] = null }
    let routerParams = { queryParams: nullQueryParams,
                         queryParamsHandling: 'merge',
                         replaceUrl: true } as NavigationExtras
    if (params) routerParams = Object.assign(routerParams, params)
    this.router.navigate([], routerParams)
  }
  removeQueryParam(queryParam: string, params?) { this.removeQueryParams([queryParam], params) }

  outletPassCheck(outlet: string): boolean {
    let pass = ['primary', 'secondary', 'p', 'm', 'l', 'c'].indexOf(outlet) > -1
    if (!pass) { console.log('no outlet:', outlet) }
    return pass
  }

  currentUrl(params) {
    let url = window.location.origin
    let outlets = this.outlets()
    let primary = outlets['primary']
    let newOutlets = params['outlets']
    let newQueryParams = params['queryParams']
    if (!outlets['primary']) { console.log('getUrl err: no primary outlet'); return null }
    url += "/" + outlets['primary']

    let outletString = this.currentOutletString(newOutlets)
    if (outletString) url += outletString
    let queryParamString = this.currentQueryParamString(newQueryParams)
    if (queryParamString) url += queryParamString
    return url
  }

  currentOutletString(newOutlets?) {
    let outlets = this.outlets(false)
    outlets = Object.assign(outlets, newOutlets)
    // console.log('cos', newOutlets, outlets)
    if (AppStatic.objectIsEmpty(outlets)) return ''
    // console.log('cos', outlets)
    let outletsArray = []
    for (const [key, val] of Object.entries(outlets)) {
      if (!!outlets[key]) outletsArray.push(key + ":" + val)
    }
    if (outletsArray.length === 0) return ''
    let out = "(" + outletsArray.join("//") + ")"
    return out
  }

  currentQueryParamString(params?) {
    let qp = this.queryParamsLocal || {}
    let qpIn = params && params['queryParams']
    let queryParams = Object.assign(qp, qpIn)
    if (AppStatic.objectIsEmpty(queryParams)) return ''
    let qps = this.queryParamString(queryParams)
    if (!qps) return ''
    return '?' + qps
  }

  queryParamString(params: any) {
    if (!params) return
    let outArray = []
    for (const [key,val] of Object.entries(params)) outArray.push(key + "=" + val)
    return outArray.join ('&')
  }

  updateFragment(fragment: string, params={}) {
    // let navigate = params['navigate']
    window.location.hash = '#' + fragment;
  }
  
  // from https://github.com/angular/angular/issues/15338
  // CAUTION: outlets likely do not work... sets existing to null
  urlTree(url: string, params = {}) {
    // let qp = params['queryParams']
    // let fragment = params['fragment'] || null
    let outlets = params['outlets']

    // note: more info: https://angular.io/api/router/Router#createurltree
    Object.assign(params, { relativeTo: null })
    // or: from https://www.bennadel.com/blog/3351-closing-secondary-router-outlet-views-from-within-the-named-route-view-components-in-angular-4-4-4.htm
    // Object.assign(params, { relativeTo: this.route.parent })
    let urlTree = this.router.createUrlTree(['', { outlets: { primary: url, secondary: null }}], params) // relativeTo: null uses origin
    // console.log('url-tree', url, urlTree.toString(), params)
    if (!urlTree) console.warn('ERR: !urlTree???', url)
    if (outlets) console.warn('TODO??? urlTree outlets', params)
    if (!urlTree || !urlTree.root || !urlTree.root.children) return urlTree
    // console.log('urlTree - REMOVING SECONDARY', url, urlTree, primary, primary.toString() )// outlets, params, urlTree.root.children[PRIMARY_OUTLET])

    // remove secondary outlets
    urlTree.root.children = { primary: urlTree.root.children[PRIMARY_OUTLET] }
    return urlTree
  }



  // urlTreeArgsFromPageData(pd: PageData): any[] {
  //   if (!pd) return
  //   // console.log('urlTreeArgs?', typeof pd, new PageData(pd))
  //   let pageKey = pd.page_key
  //   let qpViewMap = { queryParams: { view: 'map' } }
  //   let qpFromPageData = { queryParams: pd.query_params }

  //   // console.log('toUrl:::', pd, pageKey, itemCodeResource, itemCodeReference)
  //   switch (pageKey) {
  //     case 'contact':                 return [ pageKey ]
  //     case 'collection-edit':         return [ 'edit/' + pd.itemCodeFromResource() ]
  //     case 'collection-edit-items':   return [ 'edit/' + pd.itemCodeFromResource()  + '/items' ]
  //     case 'collection-new':          return [ 'new/collection', qpFromPageData ]
  //     case 'collection-preview':      return [ pd.itemCodeSlashSlug() + '/preview' ]
  //     case 'collection-preview-map':  return [ pd.itemCodeSlashSlug() + '/preview', qpViewMap ]
  //     case 'collection-show':         return [ pd.itemCodeSlashSlug() ]
  //     case 'collection-show-map':     return [ pd.itemCodeSlashSlug(), qpViewMap ]
  //     case 'cookies':                 return [ 'auth/cookies' ]
  //     case 'dashboard':               return [ 'dashboard' ]
  //     case 'dashboard-map':           return [ 'dashboard', qpViewMap ]
  //     case 'exploration-show':        return [ pd.itemCodeSlashSlug() ]
  //     case 'exploration-show-map':    return [ pd.itemCodeSlashSlug(), qpViewMap ]
  //     case 'home':                    return [ pageKey ]
  //     case 'location-edit':           return [ 'edit/' + pd.itemCodeFromResource() ]
  //     case 'location-edit-map':       return [ 'edit/' + pd.itemCodeFromResource(), qpViewMap ]
  //     case 'location-new':            return [ 'new/location' ]
  //     case 'location-new-map':        return [ 'new/location', qpViewMap ]
  //     case 'location-show':           return [ pd.itemCodeSlashSlug() ]
  //     case 'location-show-map':       return [ pd.itemCodeSlashSlug(), qpViewMap ]
  //     case 'login':                   return [ 'auth/login' ]
  //     case 'policy-credits':          return [ 'policy/credits' ]
  //     case 'policy-terms':            return [ 'policy/terms' ]
  //     case 'policy-privacy':          return [ 'policy/privacy' ]
  //     case 'post-edit-content':       return [ 'edit/' + pd.itemCodeFromResource()  + '/content' ]
  //     case 'post-edit-location':      return [ 'edit/' + pd.itemCodeFromResource()  + '/location' ]
  //     case 'post-add-location':       return [ 'edit/' + pd.itemCodeFromReference()  + '/location-new' ]
  //     case 'post-add-location-map':   return [ 'edit/' + pd.itemCodeFromReference()  + '/location-new', qpViewMap ]
  //     case 'post-edit-details':       return [ 'edit/' + pd.itemCodeFromResource()  + '/details' ]
  //     case 'post-show':               return [ pd.itemCodeSlashSlug() ]
  //     case 'post-show-map':           return [ pd.itemCodeSlashSlug(), qpViewMap ]
  //     case 'profile-about':           return [ pd.itemCodeSlashSlug() + '/about' ]
  //     case 'profile-about-map':       return [ pd.itemCodeSlashSlug() + '/about', qpViewMap ]
  //     case 'profile-settings':        return [ 'edit/' + pd.itemCodeFromResource() + '/settings' ]
  //     case 'profile-show':            return [ pd.itemCodeSlashSlug() ]
  //     case 'profile-show-map':        return [ pd.itemCodeSlashSlug(), qpViewMap ]
  //     case 'profile-stats':           return [ pd.itemCodeSlashSlug() + '/stats' ]
  //     case 'profile-following':       return [ pd.itemCodeSlashSlug() + '/following' ]
  //     case 'profile-following-map':   return [ pd.itemCodeSlashSlug() + '/following', qpViewMap ]
  //     case 'profile-followed-by':     return [ pd.itemCodeSlashSlug() + '/followed-by' ]
  //     case 'profile-followed-by-map': return [ pd.itemCodeSlashSlug() + '/followed-by', qpViewMap ]
  //     case 'profile-upvoted':         return [ pd.itemCodeSlashSlug() + '/upvoted' ]
  //     case 'profile-upvoted-map':     return [ pd.itemCodeSlashSlug() + '/upvoted', qpViewMap ]
  //     case 'profile-downvoted':       return [ pd.itemCodeSlashSlug() + '/downvoted' ]
  //     case 'profile-downvoted-map':   return [ pd.itemCodeSlashSlug() + '/downvoted', qpViewMap ]
  //     case 'profile-collections':     return [ pd.itemCodeSlashSlug() + '/collections' ]
  //     case 'reset-password':          return [ this.pathResetPasswordFromPageData(pd) ]
  //     case 'search':                  return [ 'search', qpFromPageData ]
  //     case 'verify-email':            return [ this.pathVerifyEmailFromPageData(pd) ] // 'auth/verify-email' ]
  //     default: {
  //       if (!pageKey) return
  //       console.warn('error: router.service urlTreeArgsFromPageData()', pageKey, pd, this)
  //     }
  //   }

  //   return null
  // }

  // pathResetPasswordFromPageData(pageData: PageData) {
  //   if (!pageData || !pageData.resource_obfuscated_id || !pageData.token) return // { console.warn('ERR: prp', pageData, pageData.resource, pageData.reference); return }
  //   let token = pageData['token']
  //   let id = pageData.resource_obfuscated_id
  //   if (!token || !id) return // { console.warn('ERR missing...', token, id); return }
  //   return 'auth/' + id + '/reset-password/' + token
  // }

  // pathVerifyEmailFromPageData(pageData: PageData) {
  //   if (!pageData || !pageData.resource_obfuscated_id || !pageData.token) return // { console.warn('ERR: prp', pageData, pageData.resource, pageData.reference); return }
  //   let token = pageData['token']
  //   let id = pageData.resource_obfuscated_id
  //   if (!token || !id) return // { console.warn('ERR missing...', token, id); return }
  //   return 'auth/' + id + '/verify-email/' + token
  // }

  // urlTreeFromPageData(pageData: PageData): UrlTree {
  //   // if (typeof pageData !== 'PageData') { console.warn('err: utfp type'); return }
  //   pageData = new PageData(pageData)

  //   let args = this.urlTreeArgsFromPageData(pageData)
  //   if (!args || !args[0]) { console.warn('ERR: urlTree !arg', pageData, args); return }

  //   let forbiddenPattern = /^(edit\/)*(undefined)/i
  //   // console.warn('urlTree:', args[0], args[0].match(forbiddenPattern))
  //   if (args[0].match(forbiddenPattern)) { console.warn('url forbidden!', args[0]); return }

  //   return this.urlTree.apply(this, args)
  // }

}
